import React, { useMemo, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";

const COLORS = [
  "#7B5AC4", "#10B981", "#F59E0B", "#EC4899", "#0EA5E9",
  "#8B5CF6", "#EF4444", "#14B8A6", "#F97316", "#3B82F6",
];

/**
 * Score breakdown panel — renders the factors block from the report's
 * `recommendation` object. Stacked horizontal bar (each factor's contribution),
 * plus an expandable table. Click any row to see the source competency
 * rows that drove its score.
 */
export default function ScoreBreakdown({ recommendation, competency, overallScore }) {
  const factors = recommendation?.factors || [];
  const [expandedName, setExpandedName] = useState(null);

  const total = useMemo(
    () =>
      factors.reduce((s, f) => s + (Number(f.contribution) || 0), 0),
    [factors],
  );

  // Single stacked-bar data point. Each factor becomes a colored segment.
  const stackedRow = useMemo(() => {
    const row = { name: "Overall" };
    factors.forEach((f) => {
      row[f.name] = Number(f.contribution) || 0;
    });
    return [row];
  }, [factors]);

  const competencyRowsByName = useMemo(() => {
    const m = new Map();
    (competency || []).forEach((r) => {
      const name = r?.competency;
      if (!name) return;
      if (!m.has(name)) m.set(name, []);
      m.get(name).push(r);
    });
    return m;
  }, [competency]);

  if (!factors.length) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="text-xs uppercase tracking-wide text-vf-muted font-semibold">
          How this score was computed
        </div>
        <div className="text-sm text-vf-muted mt-2">
          No factor data yet. Score breakdown appears once competency
          rows are scored.
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <div className="text-xs uppercase tracking-wide text-vf-muted font-semibold">
            How this score was computed
          </div>
          <div className="text-[11px] text-vf-muted mt-0.5">
            Each factor contributes score × weight to the breakdown total.
          </div>
        </div>
        <div className="text-right">
          {overallScore != null && (
            <div className="text-[11px] text-vf-muted">
              Overall: <span className="font-semibold text-vf-base">{Number(overallScore).toFixed(1)}</span>
            </div>
          )}
          <div className="text-[11px] text-vf-muted">
            Breakdown total: <span className="font-semibold text-vf-base">{total.toFixed(1)}</span>
          </div>
        </div>
      </div>

      <div style={{ width: "100%", height: 56 }} className="mb-3">
        <ResponsiveContainer>
          <BarChart data={stackedRow} layout="vertical" margin={{ top: 4, right: 4, bottom: 4, left: 4 }}>
            <XAxis type="number" hide domain={[0, 100]} />
            <YAxis type="category" dataKey="name" hide />
            <Tooltip
              contentStyle={{ fontSize: 12 }}
              formatter={(value, name) => [Number(value).toFixed(1), name]}
            />
            {factors.map((f, i) => (
              <Bar
                key={f.name}
                dataKey={f.name}
                stackId="a"
                fill={COLORS[i % COLORS.length]}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="flex flex-wrap gap-x-3 gap-y-1 mb-3">
        {factors.map((f, i) => (
          <div key={f.name} className="flex items-center gap-1.5 text-[11px]">
            <span
              className="inline-block w-2.5 h-2.5 rounded-sm"
              style={{ background: COLORS[i % COLORS.length] }}
            />
            <span className="text-vf-base font-medium">{f.name}</span>
            <span className="text-vf-muted">{Number(f.contribution).toFixed(1)}</span>
          </div>
        ))}
      </div>

      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200 text-[10px] text-vf-muted uppercase tracking-wide">
            <th className="text-left p-2">Factor</th>
            <th className="text-right p-2">Score</th>
            <th className="text-right p-2">Weight</th>
            <th className="text-right p-2">Contribution</th>
            <th className="text-left p-2">Detail</th>
          </tr>
        </thead>
        <tbody>
          {factors.map((f, i) => {
            const isExpanded = expandedName === f.name;
            const sourceRows = competencyRowsByName.get(f.name) || [];
            return (
              <React.Fragment key={f.name}>
                <tr
                  onClick={() => setExpandedName(isExpanded ? null : f.name)}
                  className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                >
                  <td className="p-2">
                    <div className="flex items-center gap-2">
                      <span
                        className="inline-block w-2 h-2 rounded-sm"
                        style={{ background: COLORS[i % COLORS.length] }}
                      />
                      <span className="text-vf-base font-medium">{f.name}</span>
                      <span className="text-[10px] text-vf-muted">
                        {isExpanded ? "−" : "+"}
                      </span>
                    </div>
                  </td>
                  <td className="p-2 text-right tabular-nums">{Number(f.score).toFixed(1)}</td>
                  <td className="p-2 text-right tabular-nums text-vf-muted">
                    {Math.round((Number(f.weight) || 0) * 100)}%
                  </td>
                  <td className="p-2 text-right tabular-nums font-semibold text-vf-base">
                    {Number(f.contribution).toFixed(1)}
                  </td>
                  <td className="p-2 text-xs text-vf-muted">{f.detail || "—"}</td>
                </tr>
                {isExpanded && sourceRows.length > 0 && (
                  <tr className="bg-slate-50/50">
                    <td colSpan={5} className="p-2 pl-6">
                      <div className="text-[10px] uppercase text-vf-muted font-semibold mb-1">
                        Source questions ({sourceRows.length})
                      </div>
                      <ul className="space-y-1.5">
                        {sourceRows.map((row, idx) => (
                          <li key={idx} className="text-xs">
                            <span className="text-vf-muted">Q{row.questionId}</span>{" "}
                            <span className="text-vf-base">·</span>{" "}
                            <span className={Number(row.score) >= 70 ? "text-emerald-700" : Number(row.score) >= 55 ? "text-amber-700" : "text-red-700"}>
                              score {Number(row.score).toFixed(1)}
                            </span>
                            {row.aiComment && (
                              <div className="text-vf-muted mt-0.5 leading-relaxed">{row.aiComment}</div>
                            )}
                          </li>
                        ))}
                      </ul>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
          <tr className="border-t-2 border-slate-200 bg-slate-50/40">
            <td className="p-2 text-xs text-vf-muted font-semibold uppercase">Total</td>
            <td></td>
            <td></td>
            <td className="p-2 text-right tabular-nums font-bold text-vf-base">{total.toFixed(1)}</td>
            <td></td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
